<script setup lang="ts">
import { RefreshCw, X } from '@lucide/vue'
import {
    BOARD_LANE_LIMIT_PARAM,
    BOARD_LANES,
    type BoardLane,
    boardLanePageParam,
    EXECUTION_LANES,
} from '@zerohuman/shared'
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Textarea,
    ZhEmptyState,
    ZhFormField,
    ZhGroupRow,
    ZhInput,
    ZhPageTitle,
    ZhPanel,
    ZhStatusDot,
    ZhStatusPill,
} from '@zerohuman/ui'
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { api, type Team, type WorkItemRow } from '@/api'
import BacklogLane from '@/components/BacklogLane.vue'
import ExecutionCard from '@/components/ExecutionCard.vue'
import ExecutionStatus from '@/components/ExecutionStatus.vue'
import FirstRunHint from '@/components/FirstRunHint.vue'
import LeadChatPanel from '@/components/LeadChatPanel.vue'
import ListSearchField from '@/components/ListSearchField.vue'
import WorkItemCard from '@/components/WorkItemCard.vue'
import { useListState } from '@/composables/useListState'
import { useLiveResource } from '@/composables/useLiveResource'
import { LANE_LABELS, LANE_TONES } from '@/lib/boardLanes'
import { statusLabel, statusTone } from '@/lib/statusTone'

const search = ref('')
/** '' is every team. */
const teamId = ref('')
const teams = ref<Team[]>([])
/** False until the teams are read: a saved team is kept until then, and dropped if it is not among them. */
const teamsLoaded = ref(false)
/** #633: the search and team come back on the next visit, in place before the first board read. */
const list = useListState('executions', {
    search: { ref: search },
    teamId: { ref: teamId, allowed: () => (teamsLoaded.value ? teams.value.map(team => team.id) : null) },
})
/**
 * #429: whether the team list has been read. Grouped reads every team's list, so until the teams are known it says it
 * is loading, and when they cannot be read it says so, rather than that nothing is open.
 */
const teamsState = ref<'loading' | 'ready' | 'failed'>('loading')
async function loadTeams(): Promise<void> {
    teamsState.value = 'loading'
    try {
        teams.value = await api.teams()
        teamsLoaded.value = true
        teamsState.value = 'ready'
    } catch {
        teams.value = []
        teamsState.value = 'failed'
    }
}
onMounted(loadTeams)
/**
 * The kit Select cannot hold '' as a value, so "All teams" is this stand-in inside the control alone. It maps
 * back to '' here, at the query boundary, so the board read and the close sweep still send `teamId: ''`.
 */
const ALL_TEAMS = '__all-teams__'
const teamChoice = computed(() => teamId.value || ALL_TEAMS)
function chooseTeam(choice: unknown): void {
    teamId.value = choice === null || choice === undefined || choice === ALL_TEAMS ? '' : String(choice)
}
const query = computed(() => ({ search: search.value.trim(), teamId: teamId.value }))
/**
 * Each lane shows at most this many entries a page: its standalone items, then its executions; in Backlog a parent with
 * its children counts as one. The board pages every lane on the server, so a long Backlog costs one page to read and
 * draw, and each lane turns its own pages.
 */
const LANE_PAGE_SIZE = 20
const FIRST_PAGES: Record<BoardLane, number> = { backlog: 1, ready: 1, blocked: 1, inProgress: 1, complete: 1 }
const lanePages = ref<Record<BoardLane, number>>({ ...FIRST_PAGES })
/** A new search or team starts every lane on its first page. */
watch(query, () => {
    lanePages.value = { ...FIRST_PAGES }
})
const boardQuery = computed(() => ({
    ...query.value,
    [BOARD_LANE_LIMIT_PARAM]: LANE_PAGE_SIZE,
    ...Object.fromEntries(
        BOARD_LANES.filter(lane => lanePages.value[lane] > 1).map(lane => [
            boardLanePageParam(lane),
            lanePages.value[lane],
        ]),
    ),
}))
/** The page a lane shows, as the board answered it (it keeps a page within the lane), and how many it has. */
function lanePageOf(lane: BoardLane): { page: number; pages: number } {
    return data.value?.pages?.[lane] ?? { page: 1, pages: 1 }
}
function turnPage(lane: BoardLane, by: number): void {
    lanePages.value = { ...lanePages.value, [lane]: lanePageOf(lane).page + by }
}
/**
 * The board is every execution in the enterprise, grouped on each read; every three seconds is live enough for
 * lanes that change as runs finish, as for the run page. The elapsed times still tick every second.
 */
const BOARD_READ_MS = 3000
/**
 * #429: the page shows the Board (the default, as it always was) or the Grouped view of the work lists. A local
 * choice, not stored in the URL; the view not shown is not read on the clock.
 */
type PageView = 'board' | 'grouped'
const view = ref<PageView>('board')
const { data, error, loading, refreshing, now, visible, refresh, reread } = useLiveResource(
    () => JSON.stringify(boardQuery.value),
    signal => api.executionBoard(boardQuery.value, signal),
    { everyMs: BOARD_READ_MS, active: () => view.value === 'board' },
)
/**
 * #430: the panel that talks to a team's lead, for the team in the filter, or the first team on "All teams" with a
 * picker in the panel, as Add work item chooses. A reply that changed the work list reads the view on screen again at
 * once (the Board, or the Grouped lists), dropping a read of it already in flight; the hidden view is not read for it,
 * and the three-second read carries on as the fallback (`rereadView`).
 */
const talking = ref(false)
const talkTeam = computed(() => teams.value.find(row => row.id === teamId.value) ?? teams.value[0] ?? null)
const talkLabel = computed(() => (talkTeam.value?.leadName ? `Talk to ${talkTeam.value.leadName}` : 'Talk to the lead'))
/** On "All teams" the control names the first team's lead: its hint says which team that is, and where to change it. */
const talkHint = computed(() =>
    !teamId.value && talkTeam.value ? `${talkTeam.value.name}'s lead. Pick another team in the panel.` : undefined,
)

/**
 * #429: the Grouped view. It reads each team's open list (`state=open`): every team in view, or the one the team
 * filter names, on the board's clock. A child whose parent is not on those lists (a closed parent, which still
 * holds it) has its parent read by id, so it still shows under it.
 */
const GROUPED_PAGE = 200
const teamsInView = computed(() => (teamId.value ? [teamId.value] : teams.value.map(team => team.id)))
/** A team whose open list holds more items than one read brings back: `shown` of `total` are on screen. */
type GroupedCut = { team: string; shown: number; total: number }
type GroupedRead = { rows: WorkItemRow[]; outside: WorkItemRow[]; cut: GroupedCut[] }
/** A parent read by id, or null when it cannot be read: its child then stands at the top level. */
async function parentById(id: string, signal: AbortSignal): Promise<WorkItemRow | null> {
    try {
        return (await api.workItem(id, signal)) ?? null
    } catch {
        return null
    }
}
async function readGrouped(signal: AbortSignal): Promise<GroupedRead | null> {
    if (view.value !== 'grouped') return null
    // Not knowing the teams yet is not having none: no read stands in for their lists until they are known.
    if (!teamId.value && teamsState.value !== 'ready') return null
    const inView = teamsInView.value
    const pages = await Promise.all(
        inView.map(team => api.workItems(team, { state: 'open', limit: GROUPED_PAGE }, signal)),
    )
    const rows = pages.flatMap(page => page.data)
    const cut = pages.flatMap((page, at): GroupedCut[] =>
        page.total > page.data.length ? [{ team: inView[at] ?? '', shown: page.data.length, total: page.total }] : [],
    )
    const listed = new Set(rows.map(row => row.id))
    const missing = [...new Set(rows.flatMap(row => (row.parentId && !listed.has(row.parentId) ? [row.parentId] : [])))]
    const outside = await Promise.all(missing.map(id => parentById(id, signal)))
    return { rows, outside: outside.filter((row): row is WorkItemRow => row !== null), cut }
}
const {
    data: groupedData,
    error: groupedError,
    loading: groupedLoading,
    refreshing: groupedRefreshing,
    refresh: groupedRefresh,
    reread: groupedReread,
} = useLiveResource(
    () => JSON.stringify({ view: view.value, teams: teamsInView.value, known: teamsState.value }),
    readGrouped,
    {
        everyMs: BOARD_READ_MS,
        active: () => view.value === 'grouped',
    },
)
/** A parent and the open children the lists hold under it, in list order; or an item standing alone. */
type Group = { kind: 'parent'; item: WorkItemRow; children: WorkItemRow[] } | { kind: 'item'; item: WorkItemRow }
/** One level only: an item with a parent is a child, and one with children (any, closed ones included) a parent. */
const groups = computed<Group[]>(() => {
    const read = groupedData.value
    if (!read) return []
    const listed = new Set(read.rows.map(row => row.id))
    const byId = new Map([...read.outside, ...read.rows].map(row => [row.id, row]))
    const childrenOf = new Map<string, WorkItemRow[]>()
    for (const row of read.rows) {
        if (row.parentId && byId.has(row.parentId)) {
            childrenOf.set(row.parentId, [...(childrenOf.get(row.parentId) ?? []), row])
        }
    }
    const placed = new Set<string>()
    const result: Group[] = []
    const place = (item: WorkItemRow) => {
        if (placed.has(item.id)) return
        placed.add(item.id)
        const children = childrenOf.get(item.id) ?? []
        result.push(
            children.length > 0 || (item.children?.total ?? 0) > 0
                ? { kind: 'parent', item, children }
                : { kind: 'item', item },
        )
    }
    for (const row of read.rows) {
        const parent = row.parentId ? byId.get(row.parentId) : undefined
        // A parent on the lists takes its own place in list order; one read by id, the place of its first child.
        if (!parent) place(row)
        else if (!listed.has(parent.id)) place(parent)
    }
    return result
})
/**
 * #429: In progress shows only items being worked (`in_progress`, whatever the run holding it is doing), and the
 * search field narrows the titles. Under either, a parent with a match among its children opens on its own, with only
 * those children; a parent that matches itself, with none among its children, is listed collapsed.
 */
const inProgressOnly = ref(false)
const titleSearch = computed(() => search.value.trim().toLowerCase())
const filtering = computed(() => inProgressOnly.value || titleSearch.value !== '')
function matches(item: WorkItemRow): boolean {
    return (
        (!inProgressOnly.value || item.state === 'in_progress') &&
        (titleSearch.value === '' || item.title.toLowerCase().includes(titleSearch.value))
    )
}
type ShownGroup =
    | { kind: 'parent'; item: WorkItemRow; children: WorkItemRow[]; opensByFilter: boolean }
    | { kind: 'item'; item: WorkItemRow }
const shownGroups = computed<ShownGroup[]>(() =>
    groups.value.flatMap((group): ShownGroup[] => {
        if (group.kind === 'item') return !filtering.value || matches(group.item) ? [group] : []
        if (!filtering.value) return [{ ...group, opensByFilter: false }]
        const children = group.children.filter(matches)
        if (children.length === 0 && !matches(group.item)) return []
        return [{ kind: 'parent', item: group.item, children, opensByFilter: children.length > 0 }]
    }),
)
/**
 * Which parents are open, by item id, so a parent stays open through every live read. A person's own choices are
 * kept apart from the ones made while a filter is on: turning the filter off brings back their own, which start
 * all collapsed.
 */
const opened = ref(new Set<string>())
const openedWhileFiltered = ref(new Map<string, boolean>())
watch([inProgressOnly, titleSearch], () => {
    openedWhileFiltered.value = new Map()
})
function isOpen(group: ShownGroup & { kind: 'parent' }): boolean {
    if (!filtering.value) return opened.value.has(group.item.id)
    return openedWhileFiltered.value.get(group.item.id) ?? group.opensByFilter
}
function setOpen(group: ShownGroup & { kind: 'parent' }, open: boolean): void {
    if (filtering.value) {
        openedWhileFiltered.value = new Map(openedWhileFiltered.value).set(group.item.id, open)
        return
    }
    const next = new Set(opened.value)
    if (open) next.add(group.item.id)
    else next.delete(group.item.id)
    opened.value = next
}
/** The status of the run holding an open item, or null when none holds it. */
function runStatusOf(item: WorkItemRow): string | null {
    return !item.closedAt && item.claimedByRunId ? (item.claimedByRunStatus ?? null) : null
}
/** A parent's rollup: one count per state its children are in, closed ones included, in the order an item moves. */
const ROLLUP_STATES: readonly WorkItemRow['state'][] = ['proposed', 'ready', 'held', 'in_progress', 'done', 'dropped']
function rollupOf(item: WorkItemRow): [WorkItemRow['state'], number][] {
    return ROLLUP_STATES.flatMap(state => {
        const count = item.children?.byState[state] ?? 0
        return count > 0 ? [[state, count] as [WorkItemRow['state'], number]] : []
    })
}
const groupedEmpty = computed(() => {
    if (inProgressOnly.value) {
        return { label: 'Nothing in progress', hint: 'No item on these lists is being worked right now.' }
    }
    if (titleSearch.value) {
        return { label: 'No matching work items', hint: 'Try another search or team.' }
    }
    return { label: 'No open work items', hint: 'Items added to a team’s list appear here, under their parent.' }
})
/**
 * #429: a list cut off at one read's limit says so, as the Board's Complete lane does, so a missing item, or a parent's
 * count above the rows under it, is not a surprise.
 */
const groupedCutNote = computed(() =>
    (groupedData.value?.cut ?? [])
        .map(cut => {
            const name = teams.value.find(each => each.id === cut.team)?.name
            return `Showing the first ${cut.shown} of ${cut.total} open work items on ${name ? `${name}’s` : 'one team’s'} list.`
        })
        .join(' '),
)
/** Grouped needs the teams before it can read their lists: until then it is loading, whatever its own read says. */
const groupedTeamsUnread = computed(() => !teamId.value && teamsState.value === 'failed')
const groupedWaiting = computed(
    () => (!teamId.value && teamsState.value === 'loading') || (groupedLoading.value && !groupedData.value),
)
/** After an add or another change, the view on screen reads again. */
function refreshView(): Promise<void> {
    return view.value === 'grouped' ? groupedRefresh() : refresh()
}
/**
 * #430: the lead changed the work list from the panel, so the view on screen reads again now, dropping a read of it
 * already in flight (one begun before the change could otherwise land after it). Only that view: the hidden one is
 * not on its clock, and the Board's read is the costly one.
 */
function rereadView(): Promise<void> {
    return view.value === 'grouped' ? groupedReread() : reread()
}
/** #429: the header's live status and refresh speak for the view on screen. */
const liveError = computed(() => (view.value === 'grouped' ? groupedError.value : error.value))
const viewRefreshing = computed(() => (view.value === 'grouped' ? groupedRefreshing.value : refreshing.value))
const total = computed(() => {
    const board = data.value
    return board ? EXECUTION_LANES.reduce((sum, lane) => sum + board.totals[lane], 0) : 0
})
/** The work items standing on the board on their own (#1830): the Backlog, and the ones a lone run holds or worked. */
const itemTotal = computed(() => {
    const board = data.value
    return board ? BOARD_LANES.reduce((sum, lane) => sum + board.itemTotals[lane], 0) : 0
})
/** The executions in a lane: none in Backlog, which only work items are in. */
function executionsIn(lane: BoardLane) {
    return lane === 'backlog' ? [] : (data.value?.lanes[lane] ?? [])
}
/** A lane's count: its executions and the work items standing in it on their own. */
function laneCount(lane: BoardLane): number {
    const board = data.value
    if (!board) return 0
    return (lane === 'backlog' ? 0 : board.totals[lane]) + board.itemTotals[lane]
}
/**
 * Close Completed (#557): clear finished work off the board. The count confirmed is the Complete lane's true
 * total under the current search and team filter — not the newest 50 it shows — and the API sweeps with those
 * same filters, so what is confirmed is what closes. Closing is undone per execution with Reopen, so this is a
 * plain confirm rather than the destructive one.
 */
const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`
const completeTotal = computed(() => data.value?.totals.complete ?? 0)
const confirmingClose = ref(false)
const closing = ref(false)
const closeNote = ref<string | null>(null)
const closeError = ref<string | null>(null)
const closeScope = computed(() => {
    const team = teams.value.find(row => row.id === teamId.value)
    const parts = [team ? ` for ${team.name}` : '', query.value.search ? ` matching “${query.value.search}”` : '']
    return parts.join('')
})
async function closeCompleted(): Promise<void> {
    closing.value = true
    closeError.value = null
    closeNote.value = null
    try {
        const { closed } = await api.closeCompletedExecutions(query.value)
        closeNote.value = `Closed ${plural(closed, 'execution')}. Reopen any of them from its page.`
        confirmingClose.value = false
        await refresh()
    } catch {
        closeError.value = 'Could not close the completed executions. Nothing was hidden; try again.'
    } finally {
        closing.value = false
    }
}
/**
 * #1831: a person adds an item to a team's list from the board, and approves every proposed item of the Backlog in
 * one step. Both read the board again when they land; a refusal is shown in the API's own words.
 */
const adding = ref(false)
const addError = ref<string | null>(null)
const addBusy = ref(false)
const NO_PRIORITY = '__none__'
/** #429: the kit Select cannot hold '' either, so "None" for the parent is this stand-in, sent as no parent at all. */
const NO_PARENT = '__no-parent__'
/**
 * Bound rather than written in the template: vue-tsc 3.3.14 turns a `//` in a component's attribute value into
 * invalid virtual code, which drops the type narrowing of the whole template (vuejs/language-tools#6322). It flattens
 * a copy of the component's props onto one line and rewrites every `//` in it as a comment, even inside a string, so
 * any attribute on a component triggers it: a declared prop or a fallthrough one, static or bound. Native elements
 * are not copied that way.
 */
const LINKS_EXAMPLE = 'https://example.com/brief'
const addForm = ref({
    teamId: '',
    title: '',
    kind: 'task',
    priority: NO_PRIORITY,
    body: '',
    links: '',
    parentId: NO_PARENT,
})
watch(adding, open => {
    if (open) {
        addError.value = null
        addForm.value = {
            teamId: teamId.value || (teams.value[0]?.id ?? ''),
            title: '',
            kind: 'task',
            priority: NO_PRIORITY,
            body: '',
            links: '',
            parentId: NO_PARENT,
        }
    }
})
/**
 * #429: the parents an item can be added under: the chosen team's open items that have no parent themselves (one
 * level only). Read when the dialog opens and again when the team changes, which also clears the choice.
 */
const parentChoices = ref<WorkItemRow[]>([])
/** Whether the parent choices are in, so None alone never reads as “this team has no parents” while they load. */
const parentChoicesState = ref<'loading' | 'ready' | 'failed'>('ready')
const PARENT_HELP = 'The item it sits under, such as a campaign. Optional.'
const parentHelp = computed(() => {
    if (parentChoicesState.value === 'loading') return 'Loading parents…'
    if (parentChoicesState.value === 'failed') return 'Could not load parents. The item can still be added on its own.'
    return PARENT_HELP
})
let parentRead = 0
async function readParentChoices(team: string): Promise<void> {
    const read = ++parentRead
    parentChoices.value = []
    parentChoicesState.value = 'loading'
    try {
        const page = await api.workItems(team, { state: 'open', limit: GROUPED_PAGE })
        if (read !== parentRead) return
        parentChoices.value = page.data.filter(row => !row.parentId)
        parentChoicesState.value = 'ready'
    } catch {
        // No choices but None: the item can still be added, standing alone, and the field says why.
        if (read === parentRead) parentChoicesState.value = 'failed'
    }
}
watch(
    () => (adding.value ? addForm.value.teamId : null),
    team => {
        addForm.value.parentId = NO_PARENT
        if (team) void readParentChoices(team)
    },
)
const canAdd = computed(
    () => addForm.value.teamId !== '' && addForm.value.title.trim() !== '' && addForm.value.kind.trim() !== '',
)
async function addItem(): Promise<void> {
    if (addBusy.value || !canAdd.value) return
    addBusy.value = true
    addError.value = null
    const form = addForm.value
    try {
        await api.addWorkItem(form.teamId, {
            title: form.title.trim(),
            kind: form.kind.trim(),
            state: 'ready',
            ...(form.priority === NO_PRIORITY ? {} : { priority: form.priority }),
            ...(form.body.trim() ? { body: form.body } : {}),
            links: form.links
                .split('\n')
                .map(link => link.trim())
                .filter(link => link !== ''),
            ...(form.parentId === NO_PARENT ? {} : { parentId: form.parentId }),
        })
        adding.value = false
        await refreshView()
    } catch (caught) {
        const code = caught instanceof Error && caught.message ? ` (${caught.message})` : ''
        addError.value = `Could not add the item${code}`
    } finally {
        addBusy.value = false
    }
}
/**
 * The proposed items in the Backlog shown, by team: Approve all approves exactly these, never one the search or the
 * team filter hid, so the count on the button is what is approved.
 */
const proposedByTeam = computed(() => {
    const byTeam = new Map<string, string[]>()
    for (const item of (data.value?.items.backlog ?? []).filter(row => row.proposed)) {
        byTeam.set(item.teamId, [...(byTeam.get(item.teamId) ?? []), item.id])
    }
    return byTeam
})
const proposedCount = computed(() => (data.value?.items.backlog ?? []).filter(item => item.proposed).length)
const approving = ref(false)
const approveNote = ref<string | null>(null)
async function approveAll(): Promise<void> {
    if (approving.value || proposedByTeam.value.size === 0) return
    approving.value = true
    approveNote.value = null
    try {
        let approved = 0
        for (const [team, ids] of proposedByTeam.value) {
            approved += (await api.approveAllWorkItems(team, ids)).approved
        }
        approveNote.value = `Approved ${plural(approved, 'item')}.`
        await refresh()
    } catch (caught) {
        const code = caught instanceof Error && caught.message ? ` (${caught.message})` : ''
        approveNote.value = `Could not approve every proposed item${code}`
    } finally {
        approving.value = false
    }
}
const LANE_EMPTY: Record<BoardLane, string> = {
    backlog: 'Nothing in the backlog',
    ready: 'Nothing ready',
    blocked: 'Nothing blocked',
    inProgress: 'Nothing in progress',
    complete: 'Nothing completed yet',
}
/**
 * A lane opened to the page's width, to work it in depth: its title opens it, and the close button, Escape or the
 * title again bring the five lanes back. The other lanes are not drawn meanwhile; the board reads on as before.
 */
const expandedLane = ref<BoardLane | null>(null)
const shownLanes = computed<readonly BoardLane[]>(() => (expandedLane.value ? [expandedLane.value] : BOARD_LANES))
function toggleLane(lane: BoardLane): void {
    expandedLane.value = expandedLane.value === lane ? null : lane
}
/** The Backlog's open parents, by item id: kept here so a parent stays open across reads and when the lane opens. */
const backlogOpened = ref<ReadonlySet<string>>(new Set())
function setBacklogOpen(id: string, open: boolean): void {
    const next = new Set(backlogOpened.value)
    if (open) next.add(id)
    else next.delete(id)
    backlogOpened.value = next
}
</script>

<template>
    <section class="execution-board w-full min-w-0 space-y-6 p-4 md:p-8">
        <header class="flex flex-wrap items-start justify-between gap-4">
            <div>
                <ZhPageTitle>Executions</ZhPageTitle>
                <p class="text-muted-foreground mt-1 max-w-2xl text-sm">Follow a piece of work from its first task through every handoff, branch, and retry.</p>
            </div>
            <div class="flex items-center gap-3">
                <span class="text-muted-foreground flex items-center gap-2 text-xs"><ZhStatusDot :state="liveError ? 'active' : visible ? 'complete' : 'inactive'" :pulse="visible" />{{ liveError ? 'Reconnecting…' : visible ? 'Live · every second' : 'Updates paused' }}</span>
                <Button variant="outline" size="icon-sm" :aria-label="view === 'grouped' ? 'Refresh work items' : 'Refresh executions'" :disabled="viewRefreshing" @click="refreshView"><RefreshCw :size="15" /></Button>
            </div>
        </header>
        <div v-if="error && view === 'board'" role="alert" class="text-destructive flex flex-wrap items-center justify-between gap-3 text-sm">
            <span>{{ data ? 'Updates interrupted. Showing the last received executions.' : 'Could not load executions.' }}</span>
            <Button variant="outline" size="sm" :disabled="refreshing" @click="refresh">Try again</Button>
        </div>
        <div class="flex flex-wrap items-center gap-3">
            <!-- #429: Grouped narrows work item titles only, so it says that rather than what the Board's search matches. -->
            <ListSearchField v-model="search" type="search" :aria-label="view === 'grouped' ? 'Search work items' : 'Search executions'" :placeholder="view === 'grouped' ? 'Search work item titles…' : 'Search tasks, teams, members…'" class="flex-1 sm:max-w-sm" />
            <!-- `aria-label` lands on the field the kit Select mirrors its choice on; the trigger carries it for people. -->
            <Select :model-value="teamChoice" aria-label="Filter by team" @update:model-value="chooseTeam">
                <SelectTrigger aria-label="Filter by team">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem :value="ALL_TEAMS">All teams</SelectItem>
                    <SelectItem v-for="team in teams" :key="team.id" :value="team.id">{{ team.name }}</SelectItem>
                </SelectContent>
            </Select>
            <Button v-if="!list.isDefault.value" type="button" variant="outline" size="sm" @click="list.clear()">Clear filters</Button>
            <!-- #429: Board, the default, or the work lists grouped under their parents. -->
            <div role="group" aria-label="View" class="flex items-center gap-1">
                <Button size="sm" :variant="view === 'board' ? 'secondary' : 'ghost'" :aria-pressed="view === 'board' ? 'true' : 'false'" @click="view = 'board'">Board</Button>
                <Button size="sm" :variant="view === 'grouped' ? 'secondary' : 'ghost'" :aria-pressed="view === 'grouped' ? 'true' : 'false'" @click="view = 'grouped'">Grouped</Button>
            </div>
            <Button v-if="view === 'grouped'" size="sm" :variant="inProgressOnly ? 'secondary' : 'outline'" :aria-pressed="inProgressOnly ? 'true' : 'false'" @click="inProgressOnly = !inProgressOnly">In progress</Button>
            <span v-if="data && view === 'board'" class="text-muted-foreground ml-auto text-xs tabular-nums">{{ total }} execution{{ total === 1 ? '' : 's' }}<template v-if="itemTotal > 0"> · {{ itemTotal }} work item{{ itemTotal === 1 ? '' : 's' }}</template></span>
            <Button v-if="data && view === 'board'" variant="outline" size="sm" data-testid="close-completed" :disabled="completeTotal === 0 || closing" @click="confirmingClose = true">Close completed</Button>
            <Button v-if="data" size="sm" data-testid="add-work-item" :disabled="teams.length === 0" @click="adding = true">Add work item</Button>
            <Button v-if="talkTeam" variant="outline" size="sm" data-testid="talk-to-lead" :title="talkHint" @click="talking = true">{{ talkLabel }}</Button>
        </div>
        <p v-if="approveNote" role="status" class="text-muted-foreground text-sm">{{ approveNote }}</p>
        <Dialog v-model:open="adding">
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Add a work item</DialogTitle>
                    <DialogDescription>It goes to the end of the team's list, ready for a run to claim. A task can read it, hold it and hand it on from there.</DialogDescription>
                </DialogHeader>
                <form class="space-y-3" data-testid="add-work-item-form" @submit.prevent="addItem">
                    <ZhFormField v-slot="{ controlId }" label="Team" help="Whose list it goes on." example="Marketing">
                        <Select :model-value="addForm.teamId" @update:model-value="choice => (addForm.teamId = choice === null || choice === undefined ? '' : String(choice))">
                            <SelectTrigger :id="controlId" aria-label="Team">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem v-for="team in teams" :key="team.id" :value="team.id">{{ team.name }}</SelectItem>
                            </SelectContent>
                        </Select>
                    </ZhFormField>
                    <ZhInput id="add-work-item-title" v-model="addForm.title" label="Title" placeholder="Write the launch post" />
                    <ZhInput id="add-work-item-kind" v-model="addForm.kind" label="Kind" placeholder="content" />
                    <ZhFormField v-slot="{ controlId }" label="Priority" help="Urgent items are claimed first." example="High">
                        <Select :model-value="addForm.priority" @update:model-value="choice => (addForm.priority = choice === null || choice === undefined ? NO_PRIORITY : String(choice))">
                            <SelectTrigger :id="controlId" aria-label="Priority">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem :value="NO_PRIORITY">None</SelectItem>
                                <SelectItem value="urgent">Urgent</SelectItem>
                                <SelectItem value="high">High</SelectItem>
                                <SelectItem value="medium">Medium</SelectItem>
                                <SelectItem value="low">Low</SelectItem>
                            </SelectContent>
                        </Select>
                    </ZhFormField>
                    <!-- #429: one level only, so only the team's open items with no parent of their own are offered. -->
                    <ZhFormField v-slot="{ controlId }" label="Parent" :help="parentHelp" example="Spring campaign">
                        <Select :model-value="addForm.parentId" @update:model-value="choice => (addForm.parentId = choice === null || choice === undefined ? NO_PARENT : String(choice))">
                            <SelectTrigger :id="controlId" aria-label="Parent">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem :value="NO_PARENT">None</SelectItem>
                                <SelectItem v-for="choice in parentChoices" :key="choice.id" :value="choice.id">{{ choice.title }}</SelectItem>
                            </SelectContent>
                        </Select>
                    </ZhFormField>
                    <ZhFormField v-slot="{ controlId, helpId }" label="Body" help="What the work is, in plain text." example="Two paragraphs on the launch">
                        <Textarea :id="controlId" v-model="addForm.body" rows="4" :aria-describedby="helpId" />
                    </ZhFormField>
                    <ZhFormField v-slot="{ controlId, helpId }" label="Links, one per line" help="Pages the work refers to." :example="LINKS_EXAMPLE">
                        <Textarea :id="controlId" v-model="addForm.links" rows="2" :aria-describedby="helpId" />
                    </ZhFormField>
                    <p v-if="addError" role="alert" class="text-destructive text-sm">{{ addError }}</p>
                    <DialogFooter>
                        <Button type="button" size="sm" variant="outline" @click="adding = false">Cancel</Button>
                        <Button type="submit" size="sm" :disabled="!canAdd || addBusy" data-testid="add-work-item-submit">Add</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
        <p v-if="closeNote" role="status" class="text-muted-foreground text-sm">{{ closeNote }}</p>
        <p v-if="closeError" role="alert" class="text-destructive text-sm">{{ closeError }}</p>
        <Dialog v-model:open="confirmingClose">
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Close {{ plural(completeTotal, 'completed execution') }}?</DialogTitle>
                    <DialogDescription>Every execution in the Complete lane{{ closeScope }} leaves the board, including those older than the ones shown. Their runs are not changed, each stays reachable from its link and its runs, and any of them can be reopened from its page.</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button type="button" size="sm" variant="outline" @click="confirmingClose = false">Cancel</Button>
                    <Button type="button" size="sm" :disabled="closing || completeTotal === 0" @click="closeCompleted">Close {{ completeTotal }}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        <!-- #429: the work lists grouped under their parents, parents collapsed until a person or a filter opens them. -->
        <template v-if="view === 'grouped'">
            <div v-if="groupedError" role="alert" class="text-destructive flex flex-wrap items-center justify-between gap-3 text-sm">
                <span>{{ groupedData ? 'Updates interrupted. Showing the last received work items.' : 'Could not load the work lists.' }}</span>
                <Button variant="outline" size="sm" :disabled="groupedRefreshing" @click="groupedRefresh">Try again</Button>
            </div>
            <div v-if="groupedTeamsUnread" role="alert" class="text-destructive flex flex-wrap items-center justify-between gap-3 text-sm">
                <span>Could not load the teams, so their work lists cannot be shown.</span>
                <Button variant="outline" size="sm" @click="loadTeams">Try again</Button>
            </div>
            <p v-else-if="groupedWaiting" role="status" class="text-muted-foreground py-12 text-center text-sm">Loading work items…</p>
            <ZhEmptyState v-else-if="groupedData && shownGroups.length === 0" :label="groupedEmpty.label">{{ groupedEmpty.hint }}</ZhEmptyState>
            <ZhPanel v-else-if="groupedData" aria-label="Work items by parent" class="min-w-0">
                <ul class="space-y-3 text-sm">
                    <template v-for="group in shownGroups" :key="group.item.id">
                        <li v-if="group.kind === 'parent'" :data-group-parent="group.item.id" :data-item-state="group.item.state" :data-run-status="runStatusOf(group.item) ?? undefined">
                            <ZhGroupRow :count="group.item.children?.total ?? group.children.length" :open="isOpen(group)" @update:open="open => setOpen(group, open)">
                                <template #title><span class="font-medium">{{ group.item.title }}</span></template>
                                <template #rollup>
                                    <span v-for="[state, count] in rollupOf(group.item)" :key="state" :data-rollup="state">
                                        <ZhStatusPill :tone="statusTone(state)" :pulse="false">{{ count }} {{ statusLabel(state) }}</ZhStatusPill>
                                    </span>
                                </template>
                                <template #aside>
                                    <ExecutionStatus :status="group.item.state" />
                                    <span v-if="runStatusOf(group.item)" class="text-muted-foreground flex items-center gap-1 text-xs">Run <ExecutionStatus :status="runStatusOf(group.item) ?? ''" /></span>
                                    <RouterLink :to="`/work-items/${group.item.id}`" :aria-label="`Open ${group.item.title}`" class="text-muted-foreground hover:text-primary text-xs underline underline-offset-2">Open</RouterLink>
                                </template>
                                <ul class="space-y-2">
                                    <li v-for="child in group.children" :key="child.id" :data-group-item="child.id" :data-item-state="child.state" :data-run-status="runStatusOf(child) ?? undefined" class="flex flex-wrap items-center gap-2">
                                        <RouterLink :to="`/work-items/${child.id}`" class="hover:text-primary min-w-0">{{ child.title }}</RouterLink>
                                        <ExecutionStatus :status="child.state" />
                                        <span v-if="runStatusOf(child)" class="text-muted-foreground flex items-center gap-1 text-xs">Run <ExecutionStatus :status="runStatusOf(child) ?? ''" /></span>
                                    </li>
                                </ul>
                            </ZhGroupRow>
                        </li>
                        <li v-else :data-group-item="group.item.id" :data-item-state="group.item.state" :data-run-status="runStatusOf(group.item) ?? undefined" class="flex flex-wrap items-center gap-2">
                            <RouterLink :to="`/work-items/${group.item.id}`" class="hover:text-primary min-w-0 font-medium">{{ group.item.title }}</RouterLink>
                            <ExecutionStatus :status="group.item.state" />
                            <span v-if="runStatusOf(group.item)" class="text-muted-foreground flex items-center gap-1 text-xs">Run <ExecutionStatus :status="runStatusOf(group.item) ?? ''" /></span>
                        </li>
                    </template>
                </ul>
            </ZhPanel>
            <p v-if="groupedCutNote && !groupedTeamsUnread && !groupedWaiting" data-grouped-cut class="text-muted-foreground text-center text-xs tabular-nums">{{ groupedCutNote }}</p>
        </template>
        <p v-else-if="loading && !data" role="status" class="text-muted-foreground py-12 text-center text-sm">Loading executions…</p>
        <FirstRunHint
            v-else-if="data && total === 0 && itemTotal === 0 && !search && !teamId"
            lead="No executions yet. Runs of your tasks appear here, grouped into executions."
            :action="{ label: 'Go to Tasks', to: '/tasks' }"
        />
        <ZhEmptyState v-else-if="data && total === 0 && itemTotal === 0" label="No matching executions">Try another search or team.</ZhEmptyState>
        <div v-else-if="data" :class="expandedLane ? 'grid gap-4' : 'grid gap-4 md:grid-cols-2 xl:grid-cols-5'">
            <ZhPanel v-for="lane in shownLanes" :key="lane" :data-lane="lane" :data-expanded="expandedLane === lane ? 'true' : undefined" :aria-labelledby="`lane-${lane}`" class="flex min-w-0 flex-col gap-3" @keydown.escape="expandedLane = null">
                <header class="flex items-center justify-between gap-2">
                    <h2 :id="`lane-${lane}`" class="text-sm font-medium">
                        <!-- The title opens the lane to the page's width, and closes it again. -->
                        <button type="button" class="cursor-pointer text-left hover:underline focus-visible:underline" data-testid="lane-expand" :aria-expanded="expandedLane === lane ? 'true' : 'false'" :title="expandedLane === lane ? undefined : `Open ${LANE_LABELS[lane]} at full width`" @click="toggleLane(lane)">{{ LANE_LABELS[lane] }}</button>
                    </h2>
                    <span class="flex items-center gap-2">
                        <!-- Every proposed item in the Backlog shown, and only those, becomes ready in one step (#1831). -->
                        <Button v-if="lane === 'backlog' && proposedCount > 0" size="sm" variant="outline" :disabled="approving" data-testid="approve-all" @click="approveAll">Approve all ({{ proposedCount }})</Button>
                        <ZhStatusPill :tone="LANE_TONES[lane]" :count="laneCount(lane)" :pulse="false" />
                        <Button v-if="expandedLane === lane" variant="ghost" size="icon-xs" :aria-label="`Close ${LANE_LABELS[lane]}`" data-testid="lane-close" @click="expandedLane = null"><X :size="15" /></Button>
                    </span>
                </header>
                <!-- The Backlog groups its items under their parents, in the lane and at full width alike. -->
                <BacklogLane v-if="lane === 'backlog'" :board="data" :now="now" :wide="expandedLane === lane" :opened="backlogOpened" @changed="refresh" @update:open="setBacklogOpen" />
                <!-- A work item standing alone comes first in its lane (#1830); one on an execution rides that card. -->
                <div v-else :class="expandedLane === lane ? 'grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4' : 'contents'">
                    <WorkItemCard v-for="item in data.items[lane]" :key="item.id" :item="item" :now="now" @changed="refresh" />
                    <ExecutionCard v-for="execution in executionsIn(lane)" :key="execution.id" :execution="execution" :now="now" />
                </div>
                <ZhEmptyState v-if="executionsIn(lane).length === 0 && data.items[lane].length === 0" compact>{{ LANE_EMPTY[lane] }}</ZhEmptyState>
                <nav v-if="lanePageOf(lane).pages > 1" :aria-label="`${LANE_LABELS[lane]} pages`" data-lane-pager class="flex items-center justify-between gap-2 text-xs">
                    <Button type="button" size="sm" variant="outline" :disabled="lanePageOf(lane).page <= 1" @click="turnPage(lane, -1)">Previous</Button>
                    <span class="text-muted-foreground tabular-nums">Page {{ lanePageOf(lane).page }} of {{ lanePageOf(lane).pages }}</span>
                    <Button type="button" size="sm" variant="outline" :disabled="lanePageOf(lane).page >= lanePageOf(lane).pages" @click="turnPage(lane, 1)">Next</Button>
                </nav>
            </ZhPanel>
        </div>
        <LeadChatPanel v-if="talkTeam" v-model:open="talking" :teams="teams" :team-id="talkTeam.id" :pick-team="!teamId" @changed="rereadView" />
    </section>
</template>
